import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { acceptInvite, isInviteOtpStep } from '@/api/invites.api';
import { completeVerifiedOutcome } from '@/lib/auth/complete-verified-outcome';
import { ROUTES } from '@/lib/constants/routes';

/**
 * Invite-accept mutation (US-038/US-098, FR-208). A new invitee is verified by
 * the invitation link itself, so the response is what `verify-otp` returns:
 * handled exactly as `useVerifyInviteOtp` does (store the session, or go to the
 * success screen with `signInRequired`), with no emailed code. Only the resume
 * branch of an existing unverified account answers `{ email, otpEmailSent }`;
 * that still hands off to the OTP-verify screen via route state, as before.
 */
export function useAcceptInvite(token: string) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof acceptInvite>[1]) => acceptInvite(token, payload),
    onSuccess: (result) => {
      if (isInviteOtpStep(result)) {
        navigate(ROUTES.AUTH.INVITE_VERIFY_OTP.replace(':token', token), {
          state: { email: result.email, otpEmailSent: result.otpEmailSent },
        });
        return;
      }
      completeVerifiedOutcome(result, { queryClient, navigate });
    },
  });
}
